import { useCallback, useRef, useState } from "react";
import { MCPService, AttachedFile } from "../../../services/mcpService";
import { usePostHogCapture } from "../../../hooks/usePostHogCapture";
import { POSTHOG_EVENTS } from "../../../utils/posthogEvents";

interface UseScheduleQuestionInputArgs {
  scheduleId: string;
  runnerId: string;
  initialAttachments: AttachedFile[];
  onAttachmentsChange: (files: AttachedFile[]) => void;
}

interface UseScheduleQuestionInputReturn {
  attachedFiles: AttachedFile[];
  isUploading: boolean;
  error: string | null;
  handleFileSelect: (files: FileList) => Promise<void>;
  handleRemoveFile: (uri: string) => void;
  handleMapComplete: (kmlContent: string, filename: string) => Promise<void>;
}

/**
 * Per-question attachment state for the schedule modal. Unlike the chat (which uses the global
 * aiChatSlice for the live conversation), each schedule question owns its own attachment list and
 * uploads to a persistent, schedule-keyed GCS prefix via
 * MCPService.uploadFile(file, runnerId, { kind: 'schedule', scheduleId }). The executor copies these
 * into each run's conversation folder at run time.
 */
const useScheduleQuestionInput = ({
  scheduleId,
  runnerId,
  initialAttachments,
  onAttachmentsChange,
}: UseScheduleQuestionInputArgs): UseScheduleQuestionInputReturn => {
  const [attachedFiles, setAttachedFiles] = useState<AttachedFile[]>(initialAttachments);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { capture } = usePostHogCapture();

  // commit() is the sole writer, so this ref always holds the latest list. Reading it synchronously
  // lets back-to-back uploads compose against the freshest value (not a render-closure snapshot)
  // without putting a side effect inside the state updater (which would double-fire onAttachmentsChange
  // under StrictMode). setAttachedFiles gets a plain value and onAttachmentsChange fires exactly once.
  const filesRef = useRef<AttachedFile[]>(initialAttachments);
  const commit = useCallback(
    (updater: (prev: AttachedFile[]) => AttachedFile[]) => {
      const next = updater(filesRef.current);
      filesRef.current = next;
      setAttachedFiles(next);
      onAttachmentsChange(next);
    },
    [onAttachmentsChange]
  );

  const uploadFiles = useCallback(
    async (files: File[]) => {
      if (files.length === 0) return;
      setIsUploading(true);
      setError(null);
      try {
        const uploaded = await Promise.all(
          files.map((file) => {
            capture(POSTHOG_EVENTS.CHAT_FILE_ATTACHED, {
              file_name: file.name,
              file_size: file.size,
              file_type: file.type,
            });
            // conversationId carries the runnerId for schedule uploads.
            return MCPService.uploadFile(file, runnerId, { kind: "schedule", scheduleId });
          })
        );
        commit((prev) => [...prev, ...uploaded]);
      } catch (err) {
        console.error("[ScheduleQuestion] upload error:", err);
        setError(err instanceof Error ? err.message : "Failed to upload file");
      } finally {
        setIsUploading(false);
      }
    },
    [capture, commit, runnerId, scheduleId]
  );

  const handleFileSelect = useCallback(
    async (files: FileList) => {
      await uploadFiles(Array.from(files));
    },
    [uploadFiles]
  );

  const handleRemoveFile = useCallback(
    (uri: string) => {
      commit((prev) => prev.filter((f) => f.uri !== uri));
    },
    [commit]
  );

  const handleMapComplete = useCallback(
    async (kmlContent: string, filename: string) => {
      const blob = new Blob([kmlContent], { type: "application/vnd.google-earth.kml+xml" });
      const file = new File([blob], filename || "drawn-geometry.kml", {
        type: "application/vnd.google-earth.kml+xml",
      });
      await uploadFiles([file]);
    },
    [uploadFiles]
  );

  return {
    attachedFiles,
    isUploading,
    error,
    handleFileSelect,
    handleRemoveFile,
    handleMapComplete,
  };
};

export default useScheduleQuestionInput;
